import { useState } from "react";
import { motion } from "framer-motion";
import { AuthShell } from "../components/AuthShell";
import { PhoneShowcase } from "../components/PhoneShowcase";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { PasswordStrengthMeter } from "../components/PasswordStrengthMeter";
import { passwordPolicyError } from "../lib/password";
import { DURATION, EASE_OUT_SOFT } from "../lib/motion";
import { getSessionToken } from "../lib/authSession";

/**
 * BD1 — in-flow set-password step, shown right after an OTP verify when:
 *   • forced=true  — the account is passwordless (pre-BD1 backfill). No skip:
 *     the founder's decree is that every existing account sets a password on
 *     its next login before proceeding.
 *   • forced=false — the user clicked "Forgot password?" and just re-proved
 *     inbox ownership via OTP; offering a reset here is optional (skippable).
 *
 * POSTs /api/auth/set-password with the freshly-minted Bearer token. OTP
 * sessions don't need current_password (ownership just re-proved server-side).
 */
interface SetPasswordScreenProps {
  email: string;
  forced: boolean;
  onDone: () => void;
  /** Rendered only when !forced. */
  onSkip?: () => void;
}

export function SetPasswordScreen({ email, forced, onDone, onSkip }: SetPasswordScreenProps) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const policyError = touched ? passwordPolicyError(password) : null;
  const confirmError =
    touched && confirm && confirm !== password ? "Passwords don't match." : null;
  const canSubmit =
    !passwordPolicyError(password) && confirm === password && !loading;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    setSubmitError(null);
    if (!canSubmit) return;
    setLoading(true);
    try {
      const r = await fetch("/api/auth/set-password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getSessionToken()}`,
        },
        body: JSON.stringify({ password }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setSubmitError(
          (data && (data.message || data.error)) ||
            "Could not save your password. Please try again.",
        );
        setLoading(false);
        return;
      }
      setLoading(false);
      onDone();
    } catch {
      setSubmitError("Network error. Please check your connection and try again.");
      setLoading(false);
    }
  }

  return (
    <AuthShell
      eyebrow={forced ? "One last step" : "Reset password"}
      headline="Insure smarter, live better."
      subhead="BeyondSure is India's most intelligent AI insurance advisor — in English, Hindi, and 10+ Indian languages."
      formTitle={forced ? "Secure your account." : "Set a new password."}
      showcase={<PhoneShowcase />}
    >
      <motion.form
        onSubmit={handleSubmit}
        noValidate
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: DURATION.base, ease: EASE_OUT_SOFT }}
        className="bsure-ui flex flex-col gap-5"
      >
        <p className="bsure-prose text-[13.5px] leading-[1.55] text-[var(--color-ink-60)]">
          {forced ? (
            <>
              Accounts now use a password for faster sign-in. Set one for{" "}
              <span className="text-[var(--color-ink)] font-medium">{email}</span>{" "}
              to continue — next time you can skip the email code.
            </>
          ) : (
            <>
              You've verified{" "}
              <span className="text-[var(--color-ink)] font-medium">{email}</span>.
              Choose a new password for your next sign-in.
            </>
          )}
        </p>

        {submitError ? (
          <div
            role="alert"
            aria-live="polite"
            className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-[13px] leading-snug text-red-900"
          >
            {submitError}
          </div>
        ) : null}

        <div className="flex flex-col gap-1.5">
          <Input
            label="New password"
            type="password"
            autoComplete="new-password"
            autoFocus
            placeholder="At least 8 characters, a letter and a number"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onBlur={() => setTouched(true)}
            error={policyError}
          />
          <PasswordStrengthMeter password={password} />
        </div>

        <Input
          label="Confirm password"
          type="password"
          autoComplete="new-password"
          placeholder="Type it again"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          onBlur={() => setTouched(true)}
          error={confirmError}
        />

        <Button
          type="submit"
          variant="primary"
          fullWidth
          loading={loading}
          disabled={!password || !confirm}
        >
          {forced ? "Set password and continue" : "Save new password"}
        </Button>

        {!forced && onSkip ? (
          <button
            type="button"
            onClick={onSkip}
            className="bsure-ui mt-1 text-[12.5px] text-[var(--color-ink-60)] hover:text-[var(--color-ink)] transition-colors self-start"
          >
            Skip for now
          </button>
        ) : null}
      </motion.form>
    </AuthShell>
  );
}

export default SetPasswordScreen;
